import { useEffect, useLayoutEffect } from 'react';
import { isShutterRunning, registerShutterNav, runShutter } from '@/lib/shutter/shutter-controller';
import { primeShutterSound } from '@/lib/shutter/shutter-sound';
import { isHeroVisible, setHeroVisible } from '@/lib/shutter/hero-layer-store';

const QUIET_MS = 160; // only a NEW flip waits for the previous gesture's inertia to settle
const TOP_REST_MS = 180; // Work must rest at the very top before an upward gesture flips back
const TOUCH_MIN = 40;

/**
 * Two-state flip. State A: Hero layer covers the screen and the document cannot scroll.
 * State B: Hero layer gone, Work is the top of the document. Switching only happens
 * while the shutter is fully closed, so there is never a half-visible page.
 */
export default function useHeroShutter(initialHash: string) {
  // Decide Hero visibility BEFORE the first paint, so arriving from another route never
  // flashes the page content underneath the Hero layer.
  useLayoutEffect(() => {
    const startOnHero = !initialHash || initialHash === '#top';
    setHeroVisible(startOnHero);
    if (startOnHero) window.scrollTo({ top: 0, behavior: 'instant' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {

    let locked = false; // "flip disarmed": blocks another flip, never blocks normal scrolling
    let lastInput = 0;
    let lastScrollMove = 0;
    let quietTimer: number | null = null;
    let flipDir: 1 | -1 = 1;

    const detailOpen = () => !!document.querySelector('.golden-work.is-detail');
    const busy = () => isShutterRunning();
    // A gesture in the opposite direction of the last flip is clearly new intent → re-arm instantly.
    const noteDir = (d: number) => {
      if (locked && !busy() && d !== 0 && Math.sign(d) !== flipDir) {
        locked = false;
        if (quietTimer !== null) { window.clearTimeout(quietTimer); quietTimer = null; }
      }
    };

    const releaseWhenQuiet = () => {
      const check = () => {
        if (performance.now() - lastInput >= QUIET_MS) { locked = false; quietTimer = null; } else quietTimer = window.setTimeout(check, QUIET_MS);
      };
      quietTimer = window.setTimeout(check, QUIET_MS);
    };

    /** dir 1 = Hero → Work, -1 = Work → Hero. Optional section to land on after leaving Hero. */
    const flip = (dir: 1 | -1, landOn?: string) => {
      locked = true;
      flipDir = dir;
      if (quietTimer !== null) { window.clearTimeout(quietTimer); quietTimer = null; }
      runShutter(() => {
        if (dir === 1) {
          setHeroVisible(false);
          const el = landOn ? document.getElementById(landOn) : null;
          if (el) el.scrollIntoView({ behavior: 'instant' });
          else window.scrollTo({ top: 0, behavior: 'instant' });
        } else {
          window.scrollTo({ top: 0, behavior: 'instant' });
          setHeroVisible(true);
        }
      }).then(releaseWhenQuiet);
    };

    const canFlipUp = () => !isHeroVisible() && window.scrollY <= 0 && performance.now() - lastScrollMove > TOP_REST_MS;

    const onScroll = () => { lastScrollMove = performance.now(); };

    const onWheel = (e: WheelEvent) => {
      if (detailOpen() || e.ctrlKey) return;
      lastInput = performance.now();
      if (busy()) { e.preventDefault(); return; }
      noteDir(e.deltaY);
      if (isHeroVisible()) {
        e.preventDefault();
        if (!locked && e.deltaY > 0) flip(1);
        return;
      }
      if (!locked && e.deltaY < 0 && canFlipUp()) { e.preventDefault(); flip(-1); }
    };

    let touchY: number | null = null;
    const onTouchStart = (e: TouchEvent) => { touchY = e.touches[0]?.clientY ?? null; primeShutterSound(); };
    const onTouchMove = (e: TouchEvent) => {
      if (touchY === null || detailOpen()) return;
      lastInput = performance.now();
      const dy = touchY - (e.touches[0]?.clientY ?? touchY);
      if (busy()) { e.preventDefault(); return; }
      noteDir(dy);
      if (isHeroVisible()) {
        e.preventDefault();
        if (!locked && dy >= TOUCH_MIN) { touchY = null; flip(1); }
        return;
      }
      if (!locked && dy < 0 && canFlipUp()) {
        e.preventDefault();
        if (-dy >= TOUCH_MIN) { touchY = null; flip(-1); }
      }
    };
    const onTouchEnd = () => { touchY = null; };

    const onKey = (e: KeyboardEvent) => {
      primeShutterSound();
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT|BUTTON)$/.test(t.tagName))) return;
      if (detailOpen()) return;
      const down = ['ArrowDown', 'PageDown', ' ', 'End'].includes(e.key) && !e.shiftKey;
      const up = ['ArrowUp', 'PageUp', 'Home'].includes(e.key) || (e.key === ' ' && e.shiftKey);
      if (!down && !up) return;
      lastInput = performance.now();
      if (busy()) { e.preventDefault(); return; }
      noteDir(down ? 1 : -1);
      if (isHeroVisible()) {
        e.preventDefault();
        if (down && !locked) flip(1);
        return;
      }
      if (up && !locked && window.scrollY <= 0) { e.preventDefault(); flip(-1); }
    };

    const onPointer = () => primeShutterSound();

    registerShutterNav((hash) => {
      if (busy()) return true;
      if (detailOpen()) return false;
      if (hash === 'top') {
        if (isHeroVisible()) return true;
        flip(-1);
        return true;
      }
      if (isHeroVisible()) { flip(1, hash === 'work' ? undefined : hash); return true; }
      return false;
    });

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer);
    return () => {
      registerShutterNav(null);
      setHeroVisible(false);
      if (quietTimer !== null) window.clearTimeout(quietTimer);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}